/**
 * useLiveKitRoom — SFU (LiveKit) tabanlı room hook'u.
 *
 * Mevcut useWebRTC (1-1 P2P) kullanımının yerine ileride geçmek için yan yana eklendi.
 * Eski useWebRTC dokunulmadı — feature flag ile bu hook tercih edilebilir.
 *
 * Akış:
 *   1. tokenEndpoint'e POST atılır → backend `flow/memeet-livekit-token` → JWT döner
 *   2. wsUrl + token ile Room.connect()
 *   3. Local kamera+mic publish edilir
 *   4. Remote participants takip edilir, track subscribe edildikçe `participants[]` güncellenir
 *
 * Backend payload:  { roomID, name?, canPublish?, canSubscribe?, canPublishData?, ttlSeconds? }
 * Response:         { success, token, ws_url, identity, room, expires_at }
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { applyVideoEffect, type VideoEffect } from "../components/videoEffects"; // [MEMEET-VFX]
import { buildAudioCaptureOptions, loadNoiseSuppression, saveNoiseSuppression, supportsVoiceIsolation } from './audioCaptureOptions'
import {
  Room,
  RoomEvent,
  Track,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  LocalParticipant,
  ConnectionState,
  ConnectionQuality as LKConnectionQuality,
  VideoPresets,
  LocalAudioTrack,
  LocalVideoTrack,
  type RoomOptions,
} from "livekit-client";

export interface LiveKitParticipant {
  identity: string;
  name?: string;
  isLocal: boolean;
  audioTrack?: MediaStreamTrack;
  videoTrack?: MediaStreamTrack;
  /** Screen share video track (varsa) — ayrı tile olarak render edilebilir */
  screenShareTrack?: MediaStreamTrack;
  /** Screen share audio track (Chrome'da "tab audio" paylaşıldıysa) */
  screenShareAudioTrack?: MediaStreamTrack;
  audioMuted: boolean;
  videoMuted: boolean;
  speaking: boolean;
  connectionQuality: "excellent" | "good" | "poor" | "unknown";
}

export interface UseLiveKitRoomConfig {
  /** Backend'in JWT verdiği endpoint, tam URL */
  tokenEndpoint: string;
  /** Apiollon kullanıcı auth token'ı (Bearer header) */
  authToken?: string;
  /** Backend payload override için */
  extraTokenPayload?: Record<string, unknown>;
  /** Default video resolution preset */
  videoPreset?: "h180" | "h360" | "h540" | "h720" | "h1080";
  onError?: (err: Error) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  /** Bağlantı KURULDUKTAN sonra mikrofon/kamera yayını açılamazsa (izin, cihaz meşgul, cihaz yok). */
  onMediaError?: (kind: "audio" | "video", err: unknown) => void;
}

/** Bağlantı sonrası yayın hatası — kullanıcıya gösterilmek üzere saklanır. */
export interface LiveKitMediaError {
  kind: "audio" | "video";
  /** DOMException adı: NotAllowedError, NotReadableError, NotFoundError, OverconstrainedError… */
  name: string;
  at: number;
}

export interface UseLiveKitRoomReturn {
  room: Room | null;
  connectionState: ConnectionState;
  participants: LiveKitParticipant[];
  localParticipant: LiveKitParticipant | null;
  isMicEnabled: boolean;
  isCameraEnabled: boolean;
  isScreenSharing: boolean;
  /** Kullanıcının seçtiği aktif cihaz ID'leri (device settings modal ile senkron) */
  selectedAudioInput: string | null;
  selectedVideoInput: string | null;
  selectedAudioOutput: string | null;
  /** roomID = LiveKit room name (Apiollon'daki memeet roomID ile eşleştir) */
  connect: (
    roomID: string,
    opts?: { displayName?: string; audio?: boolean; video?: boolean; audioDeviceId?: string; videoDeviceId?: string }
  ) => Promise<void>;
  disconnect: () => Promise<void>;
  toggleMic: () => Promise<void>;
  toggleCamera: () => Promise<void>;
  /** In-call cihaz değişimi — LiveKit'in switchActiveDevice'ını sarar (renegotiation gerektirmez) */
  switchDevice: (kind: "audioinput" | "videoinput" | "audiooutput", deviceId: string) => Promise<void>;
  /** withAudio=true → tarayıcı izin verirse sekme/ekran sesi de paylaşılır */
  startScreenShare: (withAudio?: boolean) => Promise<void>;
  stopScreenShare: () => Promise<void>;
  sendData: (payload: Uint8Array | string, reliable?: boolean) => Promise<void>;
  /** [MEMEET-VFX] arka plan efektini kameraya uygular (kamera yokken saklar, yayinlaninca uygular). */
  setVideoEffect: (effect: VideoEffect) => Promise<boolean>;
  noiseSuppressionEnabled: boolean;
  setNoiseSuppression: (enabled: boolean) => Promise<void>;
  /** LiveKit ağ kopmasını onarırken (Reconnecting → Reconnected) true. */
  isReconnecting: boolean;
  /** Tarayıcı autoplay engeli: uzak ses çalınamıyorsa false → kullanıcı jestiyle startAudio(). */
  canPlaybackAudio: boolean;
  startAudio: () => Promise<void>;
  /** connect() sonrasında mikrofon/kamera yayını açılamadıysa dolar; UI gösterip clearMediaError() ile siler. */
  mediaError: LiveKitMediaError | null;
  clearMediaError: () => void;
}

const PRESET_MAP = {
  h180: VideoPresets.h180,
  h360: VideoPresets.h360,
  h540: VideoPresets.h540,
  h720: VideoPresets.h720,
  h1080: VideoPresets.h1080,
};

function mapQuality(q: LKConnectionQuality): LiveKitParticipant["connectionQuality"] {
  switch (q) {
    case LKConnectionQuality.Excellent: return "excellent";
    case LKConnectionQuality.Good:      return "good";
    case LKConnectionQuality.Poor:      return "poor";
    default:                            return "unknown";
  }
}

/**
 * Per-tab unique device ID. sessionStorage tab başına izole — aynı user'ın
 * iki sekmesi/tarayıcısı farklı identity alır, LiveKit "second join kicks first"
 * davranışı tetiklenmez.
 */
function getOrCreateDeviceID(): string {
  try {
    if (typeof window === "undefined") return Math.random().toString(36).slice(2, 10);
    const KEY = "memeetLiveKitDeviceID";
    const existing = window.sessionStorage.getItem(KEY);
    if (existing) return existing;
    const fresh = Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
    window.sessionStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    return Math.random().toString(36).slice(2, 10);
  }
}

function snapshot(p: LocalParticipant | RemoteParticipant, isLocal: boolean): LiveKitParticipant {
  // Track publications iteration — Local ve Remote için farklı tip, ama runtime davranışı aynı
  const pubs: any[] = Array.from((p.trackPublications as any).values());
  const audioPub = pubs.find(t => t.kind === Track.Kind.Audio && t.source === Track.Source.Microphone);
  const videoPub = pubs.find(t => t.kind === Track.Kind.Video && t.source === Track.Source.Camera);
  const screenPub = pubs.find(t => t.kind === Track.Kind.Video && t.source === Track.Source.ScreenShare);
  const screenAudioPub = pubs.find(t => t.kind === Track.Kind.Audio && t.source === Track.Source.ScreenShareAudio);
  return {
    identity: p.identity,
    name: p.name,
    isLocal,
    audioTrack: audioPub?.track?.mediaStreamTrack,
    // [MEMEET-VFX] LocalTrack.mediaStreamTrack isleyici varsa ISLENMIS izi doner; kendi goruntumuz efektli cizilir.
    videoTrack: videoPub?.track?.mediaStreamTrack,
    screenShareTrack: screenPub?.track?.mediaStreamTrack,
    screenShareAudioTrack: screenAudioPub?.track?.mediaStreamTrack,
    audioMuted: audioPub ? audioPub.isMuted : true,
    videoMuted: videoPub ? videoPub.isMuted : true,
    speaking: p.isSpeaking,
    connectionQuality: mapQuality(p.connectionQuality),
  };
}

export function useLiveKitRoom(config: UseLiveKitRoomConfig): UseLiveKitRoomReturn {
  const roomRef = useRef<Room | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.Disconnected);
  const [participants, setParticipants] = useState<LiveKitParticipant[]>([]);
  const [localParticipant, setLocalParticipant] = useState<LiveKitParticipant | null>(null);
  const [isMicEnabled, setMicEnabled] = useState(false);
  const [isCameraEnabled, setCameraEnabled] = useState(false);
  const [isScreenSharing, setScreenSharing] = useState(false);
  const [selectedAudioInput, setSelectedAudioInput] = useState<string | null>(null);
  const [selectedVideoInput, setSelectedVideoInput] = useState<string | null>(null);
  const [selectedAudioOutput, setSelectedAudioOutput] = useState<string | null>(null);
  const [noiseSuppressionEnabled, setNoiseSuppressionEnabled] = useState<boolean>(loadNoiseSuppression());
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [canPlaybackAudio, setCanPlaybackAudio] = useState(true);
  const [mediaError, setMediaError] = useState<LiveKitMediaError | null>(null);
  const clearMediaError = useCallback(() => setMediaError(null), []);

  const refreshParticipants = useCallback(() => {
    const room = roomRef.current;
    if (!room) {
      setParticipants([]);
      setLocalParticipant(null);
      return;
    }
    setLocalParticipant(snapshot(room.localParticipant, true));
    setParticipants(Array.from(room.remoteParticipants.values()).map(p => snapshot(p, false)));
    // Mikrofon dugmesini GERCEK track durumundan tazele (2026-09-16).
    // Onceden setMicEnabled yalnizca toggleMic icinde cagriliyordu; moderator
    // sunucu tarafindan sustururdugunde (RoomService MutePublishedTrack) bu yoldan
    // gecilmedigi icin susturulan kisinin kendi dugmesi ACIK gorunuyordu —
    // kisi sustuguldugunu fark etmiyordu. TrackMuted/TrackUnmuted zaten buraya
    // dusuyor, dogru kaynak da burasi.
    setMicEnabled(room.localParticipant?.isMicrophoneEnabled === true);
  }, []);

  // [MEMEET-VFX] 2026-11-05: secili arka plan efekti. Kamera her yayinlandiginda (ilk acilis, kapatip
  // acma, yeniden baglanma) yeniden uygulanir; cihaz degisiminde LiveKit isleyiciyi kendisi tasir.
  const videoEffectRef = useRef<VideoEffect>({ mode: "none" });
  const applyCameraEffect = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return false;
    const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
    const track = pub?.videoTrack as any;
    if (!track) return false;
    const ok = await applyVideoEffect(track, videoEffectRef.current);
    refreshParticipants();
    return ok;
  }, [refreshParticipants]);
  const applyCameraEffectRef = useRef(applyCameraEffect);
  applyCameraEffectRef.current = applyCameraEffect;
  const setVideoEffect = useCallback((effect: VideoEffect) => {
    videoEffectRef.current = effect;
    return applyCameraEffect();
  }, [applyCameraEffect]);

  const attachListeners = useCallback((room: Room) => {
    const onChange = () => refreshParticipants();
    // [MEMEET-VFX] kamera yayinlaninca secili efekti uygula
    room.on(RoomEvent.LocalTrackPublished, (pub: any) => {
      if (pub?.source === Track.Source.Camera && videoEffectRef.current.mode !== "none") void applyCameraEffectRef.current();
    });

    room
      .on(RoomEvent.ParticipantConnected,    onChange)
      .on(RoomEvent.ParticipantDisconnected, onChange)
      .on(RoomEvent.TrackSubscribed, (_track: RemoteTrack, _pub: RemoteTrackPublication, _p: RemoteParticipant) => onChange())
      .on(RoomEvent.TrackUnsubscribed,       onChange)
      .on(RoomEvent.TrackMuted,              onChange)
      .on(RoomEvent.TrackUnmuted,            onChange)
      .on(RoomEvent.LocalTrackPublished,     onChange)
      .on(RoomEvent.LocalTrackUnpublished,   onChange)
      .on(RoomEvent.ActiveSpeakersChanged,   onChange)
      .on(RoomEvent.ConnectionQualityChanged, onChange)
      .on(RoomEvent.ConnectionStateChanged,  (state) => {
        setConnectionState(state);
        if (state === ConnectionState.Connected)    config.onConnected?.();
        if (state === ConnectionState.Disconnected) config.onDisconnected?.();
        onChange();
      })
      // Ağ kopması onarımı (2026-10-01): kullanıcı "donmuş" ekrana bakmasın, UI şerit gösterir.
      .on(RoomEvent.Reconnecting, () => setIsReconnecting(true))
      .on(RoomEvent.Reconnected, () => { setIsReconnecting(false); onChange(); })
      // Autoplay engeli: tarayıcı uzak sesi kullanıcı jesti olmadan çalmayı reddederse
      // (Opera/Safari'de sık) sessizce ses gitmesin; UI "Sesi aç" düğmesi gösterir.
      .on(RoomEvent.AudioPlaybackStatusChanged, () => setCanPlaybackAudio(room.canPlaybackAudio))
      .on(RoomEvent.Disconnected, () => {
        setConnectionState(ConnectionState.Disconnected);
        setParticipants([]);
        setLocalParticipant(null);
        setMicEnabled(false);
        setCameraEnabled(false);
        setScreenSharing(false);
        setIsReconnecting(false);
        setCanPlaybackAudio(true);
      });
  }, [config, refreshParticipants]);

  const connect = useCallback<UseLiveKitRoomReturn["connect"]>(async (roomID, opts) => {
    if (roomRef.current) await roomRef.current.disconnect();

    // 1) Backend'den token al
    const resp = await fetch(config.tokenEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(config.authToken ? { Authorization: `Bearer ${config.authToken}` } : {}),
      },
      body: JSON.stringify({
        roomID,
        name: opts?.displayName || "",
        canPublish: true,
        canSubscribe: true,
        canPublishData: true,
        // Per-tab unique device ID — backend identity'ye suffix olarak ekler,
        // ayni user'in 2 sekmesi/cihazinin birbirini kovmasini engeller.
        deviceID: getOrCreateDeviceID(),
        ...(config.extraTokenPayload || {}),
      }),
    });
    if (!resp.ok) throw new Error(`token_endpoint_${resp.status}`);
    const json = await resp.json();
    const result = json?.result ?? json;
    if (!result?.success || !result?.token || !result?.ws_url) {
      throw new Error(result?.error || "token_failed");
    }

    // 2) Room
    const presetKey = config.videoPreset || "h720";
    const roomOptions: RoomOptions = {
      adaptiveStream: true,
      dynacast: true,
      // Tab/window kapanışında SFU'yu derhal bilgilendir; aksi halde diğer
      // katılımcılar 30sn boyunca "ghost" peer görür.
      disconnectOnPageLeave: true,
      videoCaptureDefaults: { resolution: PRESET_MAP[presetKey].resolution },
      // Yanki bastirma oda seviyesinde de varsayilan: LiveKit cihaz degisiminde
      // (kulaklik takma/cikarma, setSinkId sonrasi yeniden publish) mikrofonu
      // kendi basina yeniden yayinlayabiliyor; o yolda da AEC acik kalsin.
      audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      publishDefaults: { videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360] },
    };
    const room = new Room(roomOptions);
    roomRef.current = room;
    attachListeners(room);

    await room.connect(result.ws_url, result.token);

    // StrictMode / hızlı remount yarışına karşı: connect döndükten sonra
    // hâlâ aynı oda referansta mı + state Connected mi? Değilse publish atla.
    if (roomRef.current !== room || room.state !== ConnectionState.Connected) return;

    const wantAudio = opts?.audio !== false;
    const wantVideo = opts?.video !== false;
    const audioDeviceId = opts?.audioDeviceId;
    const videoDeviceId = opts?.videoDeviceId;
    // 2026-10-01: Yayın hatası ARTIK FIRLATILMIYOR. Eskiden kamera/mikrofon açılamayınca
    // (izin yok, cihaz meşgul, eski cihaz kimliği) hata joinCall'a fırlıyor, UI "idle"a dönüp
    // lobiye düşüyor ama oda bağlı kalıyordu: host kişiyi içeride görürken kişi lobide bekliyor,
    // siyah ekran görüyordu. Şimdi bağlı kalır, hata mediaError ile UI'a bildirilir; mikrofon ve
    // kamera birbirinden bağımsız denenir (kamera hatası mikrofonu düşürmez).
    const publishOne = async (kind: "audio" | "video", fn: () => Promise<unknown>) => {
      try {
        await fn();
        return true;
      } catch (e) {
        if (roomRef.current !== room || room.state !== ConnectionState.Connected) return false;
        const name = (e as { name?: string })?.name || "UnknownError";
        setMediaError({ kind, name, at: Date.now() });
        config.onMediaError?.(kind, e);
        return false;
      }
    };
    setMediaError(null);
    if (wantAudio) {
      await publishOne("audio", () =>
        room.localParticipant.setMicrophoneEnabled(true, buildAudioCaptureOptions(noiseSuppressionEnabled, audioDeviceId)));
    }
    if (wantVideo) {
      await publishOne("video", () =>
        room.localParticipant.setCameraEnabled(true, videoDeviceId ? { deviceId: videoDeviceId } : undefined));
    }
    if (roomRef.current !== room) return;
    setMicEnabled(room.localParticipant.isMicrophoneEnabled === true);
    setCameraEnabled(room.localParticipant.isCameraEnabled === true);
    if (audioDeviceId) setSelectedAudioInput(audioDeviceId);
    if (videoDeviceId) setSelectedVideoInput(videoDeviceId);

    refreshParticipants();
  }, [config, attachListeners, refreshParticipants]);

  const switchDevice = useCallback<UseLiveKitRoomReturn["switchDevice"]>(async (kind, deviceId) => {
    if (kind === "audioinput") setSelectedAudioInput(deviceId);
    else if (kind === "videoinput") setSelectedVideoInput(deviceId);
    else if (kind === "audiooutput") setSelectedAudioOutput(deviceId);

    const room = roomRef.current;
    if (!room) return;
    const roomAny = room as any;
    if (typeof roomAny.switchActiveDevice !== "function") return;
    try {
      await roomAny.switchActiveDevice(kind, deviceId);
      refreshParticipants();
    } catch (e) {
      console.error("[LiveKit] switchDevice failed:", kind, e);
    }
  }, [refreshParticipants]);

  const disconnect = useCallback(async () => {
    if (roomRef.current) {
      await roomRef.current.disconnect();
      roomRef.current = null;
    }
  }, []);

  const toggleMic = useCallback(async () => {
    const room = roomRef.current; if (!room) return;
    // Toggle from the ACTUAL LiveKit track state, not the React mirror, so a
    // moderator force-mute / reconnect that changes the real state without going
    // through here can never desync the toggle (the "can't un-mute after mute" bug).
    const next = !room.localParticipant.isMicrophoneEnabled;
    if (next) {
      await room.localParticipant.setMicrophoneEnabled(true, buildAudioCaptureOptions(noiseSuppressionEnabled, selectedAudioInput || undefined));
    } else {
      await room.localParticipant.setMicrophoneEnabled(false);
    }
    setMicEnabled(next);
    refreshParticipants();
  }, [refreshParticipants, noiseSuppressionEnabled, selectedAudioInput]);

  /** Autoplay engelinde kullanıcı jestiyle çağrılır; LiveKit askıdaki ses öğelerini oynatır. */
  const startAudio = useCallback(async () => {
    const room = roomRef.current; if (!room) return;
    try { await room.startAudio(); } catch (e) { config.onError?.(e as Error); }
    setCanPlaybackAudio(room.canPlaybackAudio);
  }, [config]);

  const setNoiseSuppression = useCallback(async (enabled: boolean) => {
    setNoiseSuppressionEnabled(enabled);
    saveNoiseSuppression(enabled);
    const room = roomRef.current;
    const track = room?.localParticipant.getTrackPublication(Track.Source.Microphone)?.audioTrack;
    // Mikrofon henuz yayinda degilse bir sey yapmaya gerek yok: ilk publish zaten
    // guncel tercihle (buildAudioCaptureOptions) yapilacak.
    if (!room || !track) return;
    // TUZAK: setMicrophoneEnabled(true, options) YAYINDAKI track icin options'i YOK
    // SAYAR — LiveKit setTrackEnabled, publication varsa yalnizca unmute eder. Eski kod
    // mute+unmute yaptigi icin anahtar hicbir sey degistirmiyordu (kullanici "fark yok").
    // Kisitlar ancak track YENIDEN OLUSTURULUNCA uygulanir: restartTrack() getUserMedia'yi
    // yeni kisitlarla cagirip sender.replaceTrack ile devreder; mute durumu korunur
    // (LocalTrack.setMediaStreamTrack: `enabled = isUnmuting ? true : !this.isMuted`).
    // Cihaz kimligini acikca veriyoruz: LiveKit vermezsek `deviceId: {ideal:'default'}`
    // koyar ve isletim sistemi varsayilani degismisse BASKA mikrofona gecebilir.
    const deviceId =
      selectedAudioInput || track.mediaStreamTrack.getSettings?.().deviceId || undefined;
    const opts = buildAudioCaptureOptions(enabled, deviceId, supportsVoiceIsolation());
    const wasMuted = track.isMuted;
    try {
      await track.restartTrack(opts);
    } catch (e) {
      // Nadir: cihaz mesgul/izin dustu → track'i birakip yeniden yayinla.
      console.warn('NC restart failed, republishing:', (e as any)?.message);
      try {
        await room.localParticipant.unpublishTrack(track, true);
        await room.localParticipant.setMicrophoneEnabled(true, opts);
        if (wasMuted) await room.localParticipant.setMicrophoneEnabled(false);
      } catch (e2) {
        console.warn('NC republish failed:', (e2 as any)?.message);
      }
    }
    refreshParticipants();
  }, [selectedAudioInput, refreshParticipants]);

  const toggleCamera = useCallback(async () => {
    const room = roomRef.current; if (!room) return;
    const next = !room.localParticipant.isCameraEnabled;
    await room.localParticipant.setCameraEnabled(next);
    setCameraEnabled(next);
    refreshParticipants();
  }, [refreshParticipants]);

  const startScreenShare = useCallback(async (withAudio?: boolean) => {
    const room = roomRef.current; if (!room) return;

    if (!withAudio) {
      // Audio istenmemişse default LiveKit yolu — basit ve sade.
      await room.localParticipant.setScreenShareEnabled(true, { audio: false });
      setScreenSharing(true);
      refreshParticipants();
      return;
    }

    // Audio istenmişse: Google Meet stratejisi — displaySurface: "browser" ile
    // Chrome'a SADECE Sekme listesi göster. Tab seçildiğinde "Sekme sesini
    // paylaş" Chrome'da default işaretli gelir → audio track GARANTİLİ döner.
    // Kullanıcı iptal ederse fallback olarak hiçbir constraint'siz dene
    // (Window/Screen seçmek isterse — ama o zaman audio gelmeyebilir).
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        // @ts-expect-error displaySurface is supported in modern Chromium but TS lib lags
        video: { displaySurface: "browser" },
        audio: true,
      });
    } catch (firstErr: any) {
      // Kullanıcı iptal ettiyse dön. Tarayıcı constraint desteklemiyorsa fallback.
      if (firstErr?.name === "NotAllowedError" || firstErr?.name === "AbortError") {
        throw firstErr;
      }
      try {
        stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      } catch (err) {
        throw err;
      }
    }

    const videoTrack = stream.getVideoTracks()[0] || null;
    const audioTrack = stream.getAudioTracks()[0] || null;

    if (!videoTrack) {
      stream.getTracks().forEach((t) => t.stop());
      throw new Error("screen_share_no_video_track");
    }

    // Video track'ini ScreenShare source ile publish et
    await room.localParticipant.publishTrack(videoTrack, {
      source: Track.Source.ScreenShare,
      simulcast: false,
    });

    // Audio track varsa ScreenShareAudio source ile publish et
    if (audioTrack) {
      try {
        await room.localParticipant.publishTrack(audioTrack, {
          source: Track.Source.ScreenShareAudio,
        });

        // Chrome Tab seçimde "Sekme sesini paylaş" işaretlenmediyse silent track
        // gönderir (audio? true döner ama içerik 0). 1.5sn boyunca level örnekle,
        // sıfırsa kullanıcıyı uyar.
        window.setTimeout(() => {
          try {
            const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
            if (!AC) return;
            const ac = new AC();
            const source = ac.createMediaStreamSource(new MediaStream([audioTrack]));
            const analyser = ac.createAnalyser();
            analyser.fftSize = 1024;
            source.connect(analyser);
            const data = new Uint8Array(analyser.frequencyBinCount);
            let maxLevel = 0;
            let samples = 0;
            const interval = window.setInterval(() => {
              analyser.getByteFrequencyData(data);
              for (let i = 0; i < data.length; i++) if (data[i] > maxLevel) maxLevel = data[i];
              samples++;
              if (samples >= 30) {
                window.clearInterval(interval);
                ac.close().catch(() => undefined);
                if (maxLevel < 5) {
                  try {
                    window.alert(
                      "Ekran sesi boş geliyor.\n\nÇözüm:\n1. Paylaşımı durdur.\n2. Tekrar 'Ekran paylaş' tıkla.\n3. Tarayıcı SEKME SEÇİM ekranında ALT KÖŞEDEKİ 'Sekme sesini paylaş' onay kutusunu MUTLAKA işaretle.\n4. Sekmeyi seç ve onayla.",
                    );
                  } catch {}
                }
              }
            }, 50);
          } catch {
            // ignore
          }
        }, 500);
      } catch {
        // audio publish hatası video paylaşımını bozmasın
      }
    }

    // Kullanıcı tarayıcı UI'ında "Stop sharing" tıklarsa video track ended olur —
    // o zaman LiveKit'ten unpublish et + state temizle.
    videoTrack.addEventListener("ended", () => {
      void room.localParticipant.unpublishTrack(videoTrack).catch(() => undefined);
      if (audioTrack) {
        void room.localParticipant.unpublishTrack(audioTrack).catch(() => undefined);
      }
      setScreenSharing(false);
      refreshParticipants();
    });

    setScreenSharing(true);
    refreshParticipants();

    // Audio istendi ama gelmediyse caller component (alert via screenShareAudioTrack
    // undefined check) kullanıcıyı bilgilendirir.
  }, [refreshParticipants]);

  const stopScreenShare = useCallback(async () => {
    const room = roomRef.current; if (!room) return;
    await room.localParticipant.setScreenShareEnabled(false);
    setScreenSharing(false);
    refreshParticipants();
  }, [refreshParticipants]);

  const sendData = useCallback<UseLiveKitRoomReturn["sendData"]>(async (payload, reliable = true) => {
    const room = roomRef.current; if (!room) return;
    const data = typeof payload === "string" ? new TextEncoder().encode(payload) : payload;
    await room.localParticipant.publishData(data, { reliable });
  }, []);

  useEffect(() => {
    // disconnectOnPageLeave + pagehide/beforeunload ile çift güvence:
    // bazı tarayıcılarda SDK'nın kendi listener'ı kayboluyor (BFCache vs).
    const onUnload = () => {
      try { roomRef.current?.disconnect(); } catch {}
    };
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", onUnload);
      window.addEventListener("beforeunload", onUnload);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("pagehide", onUnload);
        window.removeEventListener("beforeunload", onUnload);
      }
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }
    };
  }, []);

  return {
    room: roomRef.current,
    connectionState,
    participants,
    localParticipant,
    isMicEnabled,
    isCameraEnabled,
    isScreenSharing,
    selectedAudioInput,
    selectedVideoInput,
    selectedAudioOutput,
    connect,
    disconnect,
    toggleMic,
    toggleCamera,
    switchDevice,
    startScreenShare,
    stopScreenShare,
    sendData,
    setVideoEffect, // [MEMEET-VFX]
    noiseSuppressionEnabled,
    setNoiseSuppression,
    isReconnecting,
    canPlaybackAudio,
    startAudio,
    mediaError,
    clearMediaError,
  };
}
